import React, { Suspense, useEffect, useLayoutEffect, useRef } from 'react';
import GlobalStyles from './theme/GlobalStyles';
import { ThemeProvider, StyleSheetManager } from 'styled-components';
import isPropValid from '@emotion/is-prop-valid';
import { ReactQueryConfig, ReactQueryConfigProvider } from 'react-query';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Auth0Provider } from '@auth0/auth0-react';
import { AuthProvider } from 'src/state/auth';
import { auth0Domain, auth0ClientID, auth0Audience } from './utils/auth';
import { lazyWithRetry } from 'src/utils/lazyWithRetry';

// Route pages are code-split. SignedInPage pulls in the whole authenticated app, so
// keeping it lazy keeps that bundle out of the auth (login/signup/callback) surface.
const AuthPage = lazyWithRetry(() => import('src/pages/Auth/AuthPage'));
const VerifyPage = lazyWithRetry(() => import('src/pages/Auth/VerifyPage'));
const BlockedPage = lazyWithRetry(() => import('src/pages/Auth/BlockedPage'));
const SignedInPage = lazyWithRetry(() => import('src/pages/SignedInPage'));
const ReportDownloadPage = lazyWithRetry(() => import('src/pages/ReportDownloadPage'));
const CallbackPage = lazyWithRetry(() =>
  import('./pages/Auth/CallbackPage').then((m) => ({ default: m.CallbackPage })),
);
const LoginPage = lazyWithRetry(() => import('./pages/Auth/LoginPage'));
const SignupPage = lazyWithRetry(() => import('./pages/Auth/SignupPage'));
import { ThemeProvider as MUIThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import muiTheme from './theme';
import useMixpanel from './utils/mixpanel/useMixPanel';
import { ApolloProvider } from '@apollo/client';
import { apolloClient } from './graphql';
import { LocalizationProvider } from '@mui/x-date-pickers';
// AdapterDateFnsV3 is the date-fns v3/v4 adapter (the unsuffixed AdapterDateFns targets v2).
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { ThemeSchemeProvider, useScheme } from 'src/state/theme';
import { AppProvider } from 'src/state/app';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { flushPendingToast } from 'src/components/Toast';
import { NavigatorBridge } from 'src/utils/navigation';
import * as Sentry from '@sentry/react';
import FullPageError from 'src/pages/ErrorPage/FullPageError';
// moment's tz.setDefault('UTC') is gone: UTC semantics now live explicitly in src/utils/date.ts.

const reactQueryConfig: ReactQueryConfig = {
  queries: {
    useErrorBoundary: false,
    refetchOnWindowFocus: false,
    retry: false,
  },
};

const RootNavigation = () => {
  const { scheme, colors, setScheme } = useScheme();
  useMixpanel();
  const isThemeSet = useRef(false);

  useLayoutEffect(() => {
    if (!isThemeSet.current) {
      const scheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      setScheme(scheme);
      isThemeSet.current = true;
    }
  }, []);

  useEffect(() => {
    flushPendingToast();
  }, []);

  return (
    <MUIThemeProvider theme={muiTheme}>
      <ThemeProvider theme={{ ...muiTheme, scheme, colors }}>
        <GlobalStyles />
        <CssBaseline />
        <NavigatorBridge />
        <Sentry.ErrorBoundary fallback={({ resetError }) => <FullPageError onRetry={resetError} />} showDialog={false}>
          <Suspense fallback={null}>
            <Routes>
              <Route path="/callback" element={<CallbackPage />} />
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/verify" element={<VerifyPage />} />
              <Route path="/blocked" element={<BlockedPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/report-centre/download/:jobId" element={<ReportDownloadPage />} />
              <Route path="*" element={<SignedInPage />} />
            </Routes>
          </Suspense>
        </Sentry.ErrorBoundary>
        <ToastContainer
          position="top-right"
          autoClose={10000}
          hideProgressBar={false}
          closeOnClick={false}
          draggable={false}
          pauseOnFocusLoss={false}
          pauseOnHover={false}
          newestOnTop
        />
      </ThemeProvider>
    </MUIThemeProvider>
  );
};

// styled-components v6 forwards every prop to the DOM by default, so custom styling
// props (px, weight, primary, active, collapsed, toggled, nowrap, …) leak onto native
// elements and trigger React "unknown attribute" warnings. Filter props for bare DOM
// elements (string tags) through @emotion/is-prop-valid — they still drive the CSS,
// they just never reach the DOM. styled(Component) wrappers (non-string targets) are
// left untouched so they keep forwarding everything.
const shouldForwardProp = (propName: string, elementToBeRendered: unknown): boolean =>
  typeof elementToBeRendered === 'string' ? isPropValid(propName) : true;

const App = () => {
  return (
    <StyleSheetManager shouldForwardProp={shouldForwardProp}>
      <ThemeSchemeProvider>
        <AppProvider>
          <ApolloProvider client={apolloClient}>
          {/* @ts-expect-error react-query v2 provider typing gap — goes away on the v5 migration */}
          <ReactQueryConfigProvider config={reactQueryConfig}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Router>
                <Auth0Provider
                  domain={auth0Domain}
                  clientId={auth0ClientID}
                  authorizationParams={{
                    audience: `${auth0Audience}`,
                    redirect_uri: `${window.location.protocol}//${window.location.host}/callback`,
                  }}
                  cacheLocation="localstorage"
                  useRefreshTokens
                >
                  <AuthProvider>
                    <RootNavigation />
                  </AuthProvider>
                </Auth0Provider>
              </Router>
            </LocalizationProvider>
          </ReactQueryConfigProvider>
          </ApolloProvider>
        </AppProvider>
      </ThemeSchemeProvider>
    </StyleSheetManager>
  );
};

export default App;
